import { SchemaEngine } from 'schema';
import type { Poll } from 'schema/poll';
import { ICState } from 'schema/state';
import { createActor, fromPromise, InspectionEvent } from 'xstate';
import { next } from '../actions/next/next';
import { ILoginProvider } from '../authTypes';
import { createPollMachine, InvokeAction, SendError, SendInitPoll, SendNextStep } from '../machine';
import { IMediaAnalytics } from '../mediaAnalyticsTypes';
import { ITrackingConstructor, TrackingEngine } from '../trackingTypes';
import { fetchAuthStatus } from './authState';
import { loadExternalLogicRules } from './externalLogicRules';
import { createPollStorage, PollStoragePollClass } from './persistentStateStorage';
import { onRetry, onTrack } from './pixelSender';
import { parsedQueryToPreinitState, parsePreinitQuery } from './preinitializedState';
import { onInitPoll, onNextStep } from './ssrSender';
import { stateHasExactPath } from './utils';

export type PollInterpreter = ReturnType<typeof createPollInterpreter>['interpreter'];

export interface InterpreterEnv {
  APP_EXTERNAL_LOGIC_RULES_API_URL: string;
  POLL_STORAGE_UPDATE_TIMEOUT: number;
  APP_IC_CONTEXT_URL?: string | undefined;
}
export interface InterpreterIntl {
  TextFieldContentHiddenIntl: string;
  RequiredAnswersNotGiven: string;
  PleaseSelectAtLeastAnswers: (args: { count: number }) => string;
  PollRankAddOther: string;
  Other: string;
  FillingFieldMandatory: (args: { label: string }) => string;
  MaxAnswersCountReached: string;
  AudioPercentToUnlockError: string;
  VideoPercentToUnlockError: string;
  TextLimitError: string;
  CardSortingRequiredError: string;
  CardSortingAllowUncompleteError: string;
}

export function createPollInterpreter({
  poll,
  variables,
  trackingEngine,
  schemaEngine,
  env,
  intl,
  sendError,
  isDebugMode,
  sendNextStep,
  sendInitPoll,
  PollStoragePool,
  OAuth,
  Tracking,
  onEvent,
  invokeAction,
  preinitPollsIc,
  mediaAnalitics,
  externalLogicRules,
}: {
  poll: Poll;
  variables: Record<string, string> | null;
  schemaEngine: SchemaEngine;
  trackingEngine: TrackingEngine;
  devTools: boolean;
  env: InterpreterEnv;
  preinitPollsIc?: string | null;
  intl: InterpreterIntl;
  onEvent?: (e: InspectionEvent) => void;
  sendError: SendError;
  isDebugMode: boolean;
  PollStoragePool: PollStoragePollClass;
  Tracking: ITrackingConstructor;
  OAuth: Pick<ILoginProvider, 'fetchUser' | 'fetchAuthorizationCode' | 'getAccessToken'>;
  mediaAnalitics: IMediaAnalytics;
  invokeAction: InvokeAction;
  sendNextStep: SendNextStep;
  sendInitPoll: SendInitPoll;
  externalLogicRules: ICState['externalLogicRules'];
}) {
  const { beforeAuthStateRestorer, persistentStateRestorer, updateStorage, dumpStateToStorage } = createPollStorage({
    poll,
    getICState: () => interpreter.getSnapshot().context.state,
    getUser: () => interpreter.getSnapshot().context.user ?? null,
    env,
    intl,
    PollStoragePool,
    variables,
  });

  const tracking = createTracking({ variables, trackingEngine, schemaEngine, poll, Tracking });

  const machine = createPollMachine({
    poll,
    tracking,
    OAuth,
    intl,
    sendError,
    isDebugMode,
    mediaAnalitics,
    invokeAction,
    externalLogicRules,
    preinitPollsIc,
  }).provide({
    actors: {
      pixelSender: fromPromise(async ({ input: { poll, state, nextState, retryCount, trackingError } }) => {
        if (!nextState) throw new Error("Can't find nextState");

        if (retryCount > 0) {
          return await onRetry({
            state,
            nextState,
            error: trackingError,
            tracking,
            poll,
            onTrackStatusUpdate: v => {
              interpreter.send({ type: 'trackStatusUpdate', ...v });
            },
          });
        }

        return await onTrack({
          previousHash: state.lastPixelHash ?? null,
          state,
          nextState,
          tracking,
          poll,
          onTrackStatusUpdate: v => {
            interpreter.send({ type: 'trackStatusUpdate', ...v });
          },
        });
      }),
      nextCalculator: fromPromise(async ({ input: { poll, state } }) => {
        if (poll.useServerPass) {
          return await onNextStep({
            sendNextStep,
            state,
            poll,
          });
        }

        const nextState = next(poll, state);
        return { poll, state: nextState };
      }),
      initServerPassPoll: fromPromise(async ({ input: { poll, state } }) => {
        return onInitPoll({ sendInitPoll, state, poll });
      }),
      beforeAuthStateRestorer: fromPromise(beforeAuthStateRestorer),
      persistentStateRestorer: fromPromise(persistentStateRestorer),
      authStateLoader: fromPromise(() => fetchAuthStatus(OAuth)),
      beforeAuthStateDumper: fromPromise(dumpStateToStorage),
      immediatelyUpdateStorageState: fromPromise(async ({ input: { state } }) => {
        return updateStorage(state, { immediately: true });
      }),
      externalLogicRulesLoader: fromPromise(async ({ input: { poll } }) =>
        loadExternalLogicRules(poll, env.APP_EXTERNAL_LOGIC_RULES_API_URL),
      ),
    },
  });

  const interpreter = createActor(machine, { inspect: onEvent });

  interpreter.subscribe(({ context, value }) => {
    if (
      !stateHasExactPath(value, 'questions.screen.auth.dumpingStateBeforeAuth') &&
      !stateHasExactPath(value, 'questions.screen.auth.redirecting')
    ) {
      void updateStorage(context.state);
    }
    if (stateHasExactPath(value, 'questions.screen.tracked') && !context.hasStarted) {
      const preinitState = parsedQueryToPreinitState(
        context.poll,
        parsePreinitQuery(new URL(location.href).searchParams, sendError),
        e => {
          sendError(e, {
            state: context.state,
            severity: 'MINOR',
            additionalInfo: { search: new URL(location.href).search },
          });
        },
      );
      for (const [questionId, value] of preinitState) {
        if ('literalValueOther' in value) {
          interpreter.send({ type: 'othersChange', questionId, value: value.literalValueOther, touchedBy: 'system' });
        } else {
          const answeredIds =
            'literalValue' in value ? [value.literalValue] : 'answerIds' in value ? value.answerIds : [value.answerId];
          interpreter.send({ type: 'change', questionId, answeredIds, touchedBy: 'system' });
        }
      }
    }
    tracking.updatePollsIc(context.state.pollsIc);
  });

  return { interpreter, tracking };
}

function createTracking({
  variables,
  trackingEngine,
  schemaEngine,
  poll,
  Tracking,
}: {
  variables: Record<string, string> | null;
  Tracking: ITrackingConstructor;
  trackingEngine: TrackingEngine;
  schemaEngine: SchemaEngine;
  poll: Poll;
}) {
  const referer = new URL(location.href);
  const { searchParams } = new URL(location.href);
  for (const [key, value] of Object.entries(variables ?? {})) referer.searchParams.append(key, value);

  const tracking = new Tracking(
    referer.toString(),
    searchParams,
    trackingEngine,
    poll,
    {
      pollsIc: '',
    },
    schemaEngine,
  );
  return tracking;
}
